import React, { useMemo } from 'react';

import { cn } from '@lyearn/style';
import { Stack } from '@lyearn/ui';

import { SelectedTagValues, TagsMenuItem } from '../../types';
import TagValueSelector from '../TagValueSelector';
import useTagsControlled from './hooks/useTagsControlled';
import { bfsTraversal } from './utils/tagTraversalUtils';

type Props = {
  className?: string;
  error?: string;
  selectedTagValues?: SelectedTagValues;
  onTagsValueChange: (tags: SelectedTagValues) => void;
  onRemoveTag: (tagId: string) => void;
  readOnly?: boolean;
  itemClassName?: string;
  isSharedItem?: boolean;
};

const TagsRender = React.memo(
  ({
    error,
    tags,
    selectedTagValues,
    onTagsValueChange,
    onRemoveTag,
    readOnly = false,
    className,
    itemClassName = 'mt-8 me-8 max-w-full',
    isSharedItem,
  }: Props & {
    tags: TagsMenuItem[];
  }) => {
    const { controlledByMap, sourceToTargetMap, tagsIdToNameMap, getFilteredSelectedValues } =
      useTagsControlled({
        tags,
        selectedTagValues,
      });

    return (
      <Stack className={cn('relative max-w-full flex-wrap', className)}>
        {tags.map((tag: TagsMenuItem) => (
          <TagValueSelector
            key={tag.id}
            className={itemClassName}
            controlledByMap={controlledByMap}
            error={error}
            getFilteredSelectedValues={getFilteredSelectedValues}
            isSharedItem={isSharedItem}
            readOnly={readOnly}
            selectedTagValues={selectedTagValues}
            sourceToTargetMap={sourceToTargetMap}
            tag={tag}
            tagsIdToNameMap={tagsIdToNameMap}
            onDeleteTag={onRemoveTag}
            onTagsValueChange={onTagsValueChange}
          />
        ))}
      </Stack>
    );
  },
);

function AttachedTags(
  props: Props & {
    tags?: TagsMenuItem[];
  },
) {
  const { tags, ...otherProps } = props;
  const tagsBFS: TagsMenuItem[] = useMemo(() => bfsTraversal(tags || []), [tags]);
  if (!tags) {
    return null;
  }

  return <TagsRender {...otherProps} tags={tagsBFS} />;
}

export default React.memo(AttachedTags);
